// 共用的網路請求 timeout + retry helper。
// 將 fetch 相容的函數包裝成具備 timeout 控制與重試語意，
// 並把網路類錯誤（TypeError: Failed to fetch / Load failed、timeout AbortError）
// 統一包成 NetworkError（帶 isNetwork: true，原始錯誤放在 cause）。
//
// 注意：本 helper 只對「fetch 真的拋錯」（網路飄移、CORS、timeout、page unload）
// 重試；HTTP 4xx/5xx 是 resolved response，不會在這裡重試，仍交由 caller 的
// .then chain 處理（維持既有 status 判斷邏輯）。

// 使用 AbortController 控制 timeout，並整合 caller 提供的 signal。
async function fetchWithTimeout(
  fetchFunc,
  url,
  options = {},
  timeoutMs = 15000,
) {
  // 極舊環境 / SSR fallback：沒有 AbortController 時直接呼叫，不做 timeout。
  if (typeof AbortController === 'undefined') {
    return fetchFunc(url, options);
  }

  const controller = new AbortController();
  const callerSignal = options.signal;
  const onCallerAbort = () => controller.abort();

  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', onCallerAbort);
    }
  }

  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetchFunc(url, {...options, signal: controller.signal});
  } finally {
    clearTimeout(timer);
    if (callerSignal) {
      callerSignal.removeEventListener('abort', onCallerAbort);
    }
  }
}

// 判斷錯誤是否可重試：timeout 觸發的 AbortError，或網路類 TypeError。
function isRetryableError(err) {
  if (!err) return false;
  if (err.name === 'AbortError') return true;
  if (err.name === 'TypeError') {
    const msg = (err.message || '').toLowerCase();
    return (
      msg.includes('fetch') ||
      msg.includes('load failed') ||
      msg.includes('network')
    );
  }
  return false;
}

// 包裝 timeout + retry + 錯誤分類。
// 任何最終失敗都統一 throw NetworkError（isNetwork: true, cause: 原始錯誤）。
async function fetchWithRetry(
  fetchFunc,
  url,
  options = {},
  {retries = 0, timeoutMs = 15000} = {},
) {
  const callerSignal = options.signal;
  let lastErr;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetchWithTimeout(fetchFunc, url, options, timeoutMs);
    } catch (err) {
      lastErr = err;

      // caller 主動 abort：尊重 caller，停止重試。
      if (callerSignal && callerSignal.aborted) {
        break;
      }

      const canRetry = isRetryableError(err) && attempt < retries;
      if (!canRetry) {
        break;
      }

      // backoff：600 * (attempt + 1) ms（600ms、1200ms、...）。
      await new Promise((resolve) => setTimeout(resolve, 600 * (attempt + 1)));
    }
  }

  throw Object.assign(new Error('NetworkError'), {
    cause: lastErr,
    isNetwork: true,
  });
}

module.exports = {fetchWithTimeout, fetchWithRetry};
